import { api } from './api';

export interface DeliveryZone {
  id: number;
  name: string;
  description: string;
  delivery_fee: number;
  delivery_fee_formatted: string;
  is_active: boolean;
  display_order: number;
  postcodes: string[];
  postcodes_count: number;
  orders_count: number;
  delivery_slots_count: number;
  created_at: string;
  updated_at: string;
}

export interface DeliveryZoneFormData {
  name: string;
  description: string;
  delivery_fee: number;
  is_active: boolean;
  display_order: number;
  postcodes: string[];
}

export interface DeliveryZoneFilters {
  search?: string;
  is_active?: boolean;
  fee_min?: number;
  fee_max?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
  per_page?: number;
  page?: number;
}

export interface DeliveryZoneResponse {
  data: DeliveryZone[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  filters?: {
    fee_range: {
      min: number;
      max: number;
    };
  };
}

class DeliveryZoneService {
  /**
   * Get all delivery zones with optional filters
   */
  async getDeliveryZones(filters: DeliveryZoneFilters = {}): Promise<DeliveryZoneResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/v1/admin/delivery-zones?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get a single delivery zone by ID
   */
  async getDeliveryZone(id: number): Promise<{ data: DeliveryZone }> {
    const response = await api.get(`/v1/admin/delivery-zones/${id}`);
    return response.data;
  }

  /**
   * Create a new delivery zone
   */
  async createDeliveryZone(data: DeliveryZoneFormData): Promise<{ message: string; data: DeliveryZone }> {
    const response = await api.post('/v1/admin/delivery-zones', data);
    return response.data;
  }

  /**
   * Update an existing delivery zone
   */
  async updateDeliveryZone(id: number, data: DeliveryZoneFormData): Promise<{ message: string; data: DeliveryZone }> {
    const response = await api.put(`/v1/admin/delivery-zones/${id}`, data);
    return response.data;
  }

  /**
   * Delete a delivery zone
   */
  async deleteDeliveryZone(id: number): Promise<{ message: string }> {
    const response = await api.delete(`/v1/admin/delivery-zones/${id}`);
    return response.data;
  }

  /**
   * Get delivery zone by postcode (public endpoint for checkout; do not send auth to avoid server errors).
   */
  async getDeliveryZoneByPostcode(postcode: string): Promise<{ data: DeliveryZone | null }> {
    const response = await api.get(`/v1/delivery-zones/by-postcode?postcode=${encodeURIComponent(postcode)}`, { skipAuth: true } as any);
    return response.data;
  }

  /**
   * Get all active delivery zones
   */
  async getActiveDeliveryZones(): Promise<{ data: DeliveryZone[] }> {
    const response = await api.get('/v1/admin/delivery-zones/active');
    return response.data;
  }

  /**
   * Toggle delivery zone status
   */
  async toggleDeliveryZoneStatus(id: number): Promise<{ message: string; data: DeliveryZone }> {
    const response = await api.put(`/v1/admin/delivery-zones/${id}/toggle-status`);
    return response.data;
  }

  /**
   * Calculate delivery fee for a postcode
   */
  async calculateDeliveryFee(postcode: string): Promise<number> {
    try {
      const response = await this.getDeliveryZoneByPostcode(postcode);
      return response.data?.delivery_fee || 0;
    } catch (error) {
      console.warn('Could not calculate delivery fee for postcode:', postcode, error);
      return 0;
    }
  }

  /**
   * Validate delivery address (public endpoint; do not send auth to avoid ERR_HTTP2_PROTOCOL_ERROR on some hosts).
   */
  async validateDeliveryAddress(postcode: string, suburb: string): Promise<{
    is_valid: boolean;
    message: string;
    zone?: any;
    fee: number;
  }> {
    try {
      const response = await api.post('/v1/delivery-zones/validate-address', { postcode, suburb }, { skipAuth: true } as any);
      return response.data.data;
    } catch (error: any) {
      console.warn('Could not validate delivery address:', error);
      return {
        is_valid: false,
        message: error.response?.data?.message || 'Unable to validate delivery address',
        fee: 0
      };
    }
  }
}

export const deliveryZoneService = new DeliveryZoneService(); 